import { Button, Form } from 'react-bootstrap';
import React, { useContext } from 'react';
import { IBuild } from '../models/Build/Builld';
import RootStoreContext from '../models/StoresProviderContext';
import { useShowModal, useShowModalSpinner } from '@yakoffice/custom-modal';
import { useParams } from 'react-router-dom';
import { validateEmail } from '../core/validateEmailAddress';
import { RichTextEditor } from '../views/components/richTextEditor/richTextEditor';

export interface IBuildsController {
    handleMakeBuildPublic: (build: IBuild, onSuccessCallback: () => void) => void;
    handleShareBuild: (build: IBuild) => void;
    handleAddReleaseNotes: (build: IBuild, onSuccessCallback: () => void) => void;
    handleClearReleaseNotes: (build: IBuild, onSuccessCallback: () => void) => void;
}

export const useBuildsController = () => {

    const rootStore = useContext(RootStoreContext);
    const showModal = useShowModal();
    const showModalSpinner = useShowModalSpinner();
    const { projectId } = useParams<{ projectId: string }>();

    const handleMakeBuildPublic = async (build: IBuild, onSuccessCallback: () => void) => {

        if (build.isPublic) {
            await rootStore.buildStore.makeBuildPrivate(projectId, build.id);
        } else {
            await rootStore.buildStore.makeBuildPublic(projectId, build.id);
        }
        onSuccessCallback();
    };

    const handleShareBuild = async (build: IBuild) => {

        let emailAddressString: string;

        const shareBuild = async () => {

            if (emailAddressString === '' || emailAddressString === undefined || emailAddressString === null)
                return;

            const emailAddressAsStringArray = emailAddressString.split(',');

            const updatedEmailAddressAsStringArray = emailAddressAsStringArray.map(emailAddress => emailAddress.trim());

            showModal(
                {
                    show: true,
                    title: `Share build`,
                    body: `Sharing build ${build.id} with recipients`,
                    canClose: false
                }
            );

            await rootStore.buildStore.shareBuild(projectId, build.id, updatedEmailAddressAsStringArray.filter(emailAddress => validateEmail(emailAddress)));

            showModal(
                {
                    show: true,
                    title: `Share build`,
                    body: `Sharing complete.`,
                    canClose: true
                }
            );
        };

        const body = <>
            <Form.Label htmlFor="emailAddresses">Email address of the recipient</Form.Label>
            <Form.Control
                type="email"
                id="emailAddresses"
                required
                onChange={(e) => emailAddressString = e.target.value}
            />
            <Form.Text id="emailAddressesHelpText" muted>
                You can enter multiple email addresses separated by a comma.
            </Form.Text>
        </>;

        showModal(
            {
                show: true,
                title: `Share build`,
                body: body,
                action: <Button variant="primary" onClick={() => shareBuild()}>Share</Button>,
                canClose: true
            }
        );
    };

    const handleAddReleaseNotes = async (build: IBuild, onSuccessCallback: () => void) => {

        let releaseNotes = build.releaseNotes;

        const addReleaseNotes = async () => {

            if (releaseNotes === '' || releaseNotes === undefined || releaseNotes === null)
                return;


            showModalSpinner('Adding release notes', `Updating build ${build.id}. Please wait.`);

            await rootStore.buildStore.addReleaseNotesToBuild(projectId, build.id, releaseNotes);

            showModal(
                {
                    show: false
                }
            );

            onSuccessCallback();
        };

        const body = <RichTextEditor onChange={(e) => releaseNotes = e.target.value} value={build.releaseNotes} />;

        showModal(
            {
                show: true,
                title: `Add release notes`,
                body: body,
                action: <Button variant="primary" onClick={() => addReleaseNotes()}>Save</Button>,
                canClose: true
            }
        );
    };

    const handleClearReleaseNotes = async (build: IBuild, onSuccessCallback: () => void) => {
        await rootStore.buildStore.clearReleaseNotesFromBuild(projectId, build.id);
        onSuccessCallback();
    };

    const controller: IBuildsController = {
        handleMakeBuildPublic: handleMakeBuildPublic,
        handleShareBuild: handleShareBuild,
        handleAddReleaseNotes: handleAddReleaseNotes,
        handleClearReleaseNotes: handleClearReleaseNotes
    };

    return controller;

};
